import React, { useState } from "react";
import { Table, Pagination, Loader, Pill } from "@theorgchartdev/storybook";
import cx from "classnames";
import { PlanNameText } from "./PlanNameText";
import { PLACEHOLDER_TABLE_VALUE, TABLE_SELECTED_ROW_STYLE } from "@/Utils/Constants";
import { appstr } from "@coreapp/utils/appstr";
import { getLocalizedPaginationStrings, getLocalizedTableHeaderStrings } from "@/Utils/Utils";
import { PlanObjectiveColors } from "@/Utils/Plan/ObjectivesHandler";
import ApprovalStatusRenderer from "./ApprovalStatusRenderer";
import ApproversRenderer from "./ApproversRenderer";

const PlanTable = ({
  allPlans,
  statusController,
  selectedPlan,
  onPlanClick,
  onPlanAction,
  onSaveRename,
  showApprovalStatusColumn,
  onOpenApprovalModal
}) => {
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [paginatedIndices, setPaginatedIndices] = useState([]);

  const commonColumnOptions = {
    getValue: (planData, fieldName) => {
      return planData[fieldName];
    }
  };

  const dateColumnOptions = {
    getValue: (planData, fieldName) => {
      return new Date().convertUTCDateToLocalDateString(planData[fieldName]);
    },
    getSortValue: value => {
      return value.slice(0, -4);
    }
  };

  const localizedColumnOptions = {
    getValue: (planData, fieldName) => {
      return appstr(planData[fieldName]);
    },
    getSortValue: value => {
      return appstr(value);
    }
  };

  const gridOptions = {
    placeHolder: PLACEHOLDER_TABLE_VALUE,
    getRowStyle: (data, _rowIndex, isHovered) => {
      if (data.ID === selectedPlan) {
        return TABLE_SELECTED_ROW_STYLE;
      } else if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: (event, data) => {
      // This is pretty hacky, bascially we want to prevent the onPlanClick event from eating the click event when the user is trying to click on the additional options button in the plan row.
      const testId = `plan-explorer-label-${data.name}-addt-options`;
      const target = event.target;
      if (target instanceof Element && target.closest(`[data-testid="${CSS.escape(testId)}"]`)) {
        return;
      }
      onPlanClick(data.ID, event);
    }
  };

  const PlanLabel = ({ rowState }) => {
    return (
      <PlanNameText
        rowState={rowState}
        onRename={onSaveRename}
        handlePlanAction={onPlanAction}
        selectedPlan={selectedPlan}
        dataTestId={"plan-explorer-label-" + rowState?.data?.name}
      />
    );
  };

  const ObjectivePill = ({ rowState }) => {
    const objective = rowState.data.objective;

    return (
      <Pill
        label={appstr(objective)}
        color={PlanObjectiveColors[objective]}
        className="ocn-plan-explorer-objective-pill ocn-plan-explorer-cell-pill"
      />
    );
  };

  const ApprovalStatusCell = ({ rowState }) => {
    const planData = rowState.data;

    if (!planData.enablePlanApprovals) {
      return "--";
    }

    const submissionData = planData.latestSubmission;

    const onOpenModal = () => {
      onOpenApprovalModal(submissionData);
    };

    return (
      <ApprovalStatusRenderer
        submissionData={submissionData}
        onClick={submissionData ? onOpenModal : undefined}
        dataTestIdPrefix={"plan-explorer-label-" + planData.name}
      />
    );
  };

  const approvalStatusColumn = {
    field: "approvalStatusSortValue",
    headerName: appstr("Approval status"),
    type: "cellRenderer",
    columnWidth: "five",
    columnOptions: commonColumnOptions,
    CellRenderer: ApprovalStatusCell
  };

  const ApproversCell = ({ rowState }) => {
    const planData = rowState.data;

    const submissionData = planData.latestSubmission;
    const approvers = submissionData?.reviewers || [];

    if (!planData.enablePlanApprovals || approvers.length === 0) {
      return "--";
    }

    const onOpenModal = () => {
      onOpenApprovalModal(submissionData);
    };

    return (
      <ApproversRenderer
        approvers={approvers}
        onClick={submissionData ? onOpenModal : undefined}
        dataTestIdPrefix={"plan-explorer-label-" + planData.name}
      />
    );
  };

  const approversColumn = {
    field: "latestSubmission",
    headerName: appstr("Approvers"),
    type: "cellRenderer",
    columnWidth: "five",
    columnOptions: commonColumnOptions,
    filterDisabled: true,
    CellRenderer: ApproversCell
  };

  const planHeaders = [
    {
      field: "name",
      headerName: appstr("Plan Name"),
      type: "cellRenderer",
      columnWidth: "twenty",
      CellRenderer: PlanLabel
    },
    ...(showApprovalStatusColumn ? [approvalStatusColumn, approversColumn] : []),
    {
      field: "status",
      headerName: appstr("Plan state"),
      type: "defaultCol",
      columnWidth: "five",
      columnOptions: localizedColumnOptions,
      defaultFilters: [appstr("Active"), appstr("Locked")]
    },
    {
      field: "objective",
      headerName: appstr("Objective"),
      type: "cellRenderer",
      columnWidth: "five",
      columnOptions: commonColumnOptions,
      CellRenderer: ObjectivePill
    },
    {
      field: "role",
      headerName: appstr("Role"),
      type: "defaultCol",
      columnWidth: "five",
      columnOptions: commonColumnOptions
    },
    {
      field: "createDate",
      headerName: appstr("Created date"),
      columnWidth: "ten",
      type: "defaultCol",
      columnOptions: dateColumnOptions
    },
    {
      field: "modDate",
      headerName: appstr("Modified date"),
      type: "defaultCol",
      columnWidth: "ten",
      columnOptions: dateColumnOptions
    }
  ];

  return (
    <>
      <div className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}>
        <Table
          tableLayout={"fixed"}
          data={allPlans}
          columns={planHeaders}
          sortEnabled={true}
          filterEnabled={true}
          onFilterChange={setFilteredPlans}
          containerPadding={true}
          gridOptions={gridOptions}
          rowSize={"largeR"}
          textSize={"mediumT"}
          paginatedIndices={paginatedIndices}
          dataTestId="chart-mode-explorer-plans-table"
          headerControlProps={{
            textTags: getLocalizedTableHeaderStrings()
          }}
        />
        {statusController.isLoading && (
          <Loader.Container dataTestId="plan-explorer-overlay">
            <Loader.Spinner />
            <Loader.Text>{statusController.message ?? appstr("Loading")}</Loader.Text>
          </Loader.Container>
        )}
      </div>
      <div className="directory-pagination" style={{ marginBottom: "0" }}>
        <Pagination
          modifier="full"
          data={filteredPlans}
          onDataChange={setPaginatedIndices}
          customStyle={{ padding: "12px 24px" }}
          startPage={1}
          dataTestIdPrefix="chart-mode-explorer-plans-pagination-"
          textTags={getLocalizedPaginationStrings()}
          preserveSelectedPage
        />
      </div>
    </>
  );
};

export default PlanTable;
